<template>
  <GloboGraphicsModulo icon="mdi-playlist-edit" title="Editor" :offline="offline">
    <!-- Field filter — only worth the space once a template has many fields -->
    <template v-if="showFieldSearch" #subheader>
      <div class="gg-editor-sub">
        <div class="gg-editor-search">
          <input
            v-model="search"
            type="text"
            class="gg-editor-search__input"
            placeholder="Digite o campo ou informação para buscar"
            aria-label="Buscar campo ou informação"
          />
          <span class="gg-editor-search__icon" aria-hidden="true">
            <v-icon size="20">mdi-magnify</v-icon>
          </span>
        </div>
      </div>
    </template>

    <div v-if="!item" class="gg-empty-box">
      <span class="gg-empty-box__text">Selecione uma playlist, template ou cena para editar</span>
    </div>

    <template v-else>
      <p class="gg-editor__note">
        <span v-if="isNative">Edita a cena reutilizável — a alteração vale para todas as playlists que a usam.</span>
        <span v-else>Sua edição tem precedência até a próxima atualização da redação (MOS).</span>
      </p>

      <GloboGraphicsSceneFields
        :model="model"
        :field-values="fieldValues"
        :loading="loadingModel"
        :search="search"
        @field-input="onFieldInput"
        @open-image-picker="openImagePicker"
      />

      <p class="gg-editor__status" :class="`gg-editor__status--${saveState}`">
        <template v-if="saveState === 'saving' || saveState === 'pending'">
          <v-progress-circular indeterminate size="12" width="2" class="mr-2" />
          Salvando…
        </template>
        <template v-else-if="saveState === 'saved'">
          <v-icon size="14" class="mr-1">mdi-check</v-icon>
          Salvo automaticamente
        </template>
        <template v-else-if="saveState === 'error'">
          <v-icon size="14" class="mr-1">mdi-alert-circle-outline</v-icon>
          {{ saveError }}
        </template>
      </p>

      <GloboGraphicsImagePicker
        v-if="showImagePicker"
        :visible="showImagePicker"
        :field-name="imagePickerFieldName"
        :cod_producao="codProducao"
        @close="showImagePicker = false"
        @select="handleImageSelected"
      />
    </template>
  </GloboGraphicsModulo>
</template>

<script>
/**
 * Editor block — edits the fields of whatever is selected in Playlists or in
 * Templates e cenas.
 * Two save paths by item kind:
 *  - MOS (inline) item → playout/updatePlaylistItemValues (operator edit wins until
 *    the next newsroom update).
 *  - Native item (reusable cena) → globographics/saveVizrtCena (applies to every
 *    playlist that uses the cena).
 *
 * Edits persist on their own: a change queues a save that fires once typing
 * pauses. Switching selection flushes anything still queued, so the save always
 * lands on the item it was typed into rather than the newly selected one.
 */
import { mapActions } from 'vuex'
import GloboGraphicsModulo from './GloboGraphicsModulo.vue'
import GloboGraphicsSceneFields from './GloboGraphicsSceneFields.vue'
import GloboGraphicsImagePicker from './GloboGraphicsImagePicker.vue'

const AUTOSAVE_DELAY = 800
// Below this the form is short enough to scan; the filter would only take space.
const FIELD_SEARCH_MIN_FIELDS = 8

export default {
  name: 'GloboGraphicsInlineEditor',
  components: { GloboGraphicsModulo, GloboGraphicsSceneFields, GloboGraphicsImagePicker },
  props: {
    item: { type: Object, default: null },
    idPlaylist: { type: Number, default: null },
    codProducao: { type: Number, default: null },
    offline: { type: Boolean, default: false },
  },
  data() {
    return {
      model: null,
      fieldValues: {},
      loadingModel: false,
      search: '',
      saveState: 'idle',
      saveError: null,
      saveTimer: null,
      queuedContext: null,
      saveSeq: 0,
      showImagePicker: false,
      imagePickerFieldName: null,
    }
  },
  computed: {
    templateId() {
      return this.item?.dsc_vizrt_template_id || this.item?.cena?.dsc_vizrt_template_id || null
    },
    // Native items carry a reusable cena; their values live on the cena and save
    // through the cena editor, not the inline MOS-item endpoint.
    isNative() {
      return this.item?.id_gh_cena != null
    },
    // Playlist items and saved cenas identify themselves on different keys, so the
    // reseed watcher needs both — a cena has no playlist-item id at all.
    itemKey() {
      if (!this.item) return ''
      return `${this.item.id_playlist_item || ''}:${this.item.id_gh_cena || ''}`
    },
    showFieldSearch() {
      return (this.model?.fields?.length || 0) > FIELD_SEARCH_MIN_FIELDS
    },
  },
  watch: {
    itemKey: {
      immediate: true,
      handler() {
        this.flushQueuedSave()
        this.seed()
      },
    },
  },
  beforeDestroy() {
    this.flushQueuedSave()
  },
  methods: {
    ...mapActions({
      fetchVizrtTemplateModel: 'globographics/fetchVizrtTemplateModel',
      updatePlaylistItemValues: 'playout/updatePlaylistItemValues',
      saveVizrtCena: 'globographics/saveVizrtCena',
      fetchPlaylists: 'playout/fetchPlaylists',
    }),
    async seed() {
      this.search = ''
      this.saveState = 'idle'
      this.saveError = null
      if (!this.item) {
        this.model = null
        this.fieldValues = {}
        return
      }
      // Deep-copy the current values so edits don't mutate list state until saved.
      const source = this.isNative ? this.item?.cena?.dsc_valores_campos : this.item?.dsc_valores_campos
      this.fieldValues = source ? JSON.parse(JSON.stringify(source)) : {}
      this.model = null
      if (!this.templateId) return
      this.loadingModel = true
      try {
        this.model = await this.fetchVizrtTemplateModel(this.templateId)
      } finally {
        this.loadingModel = false
      }
    },
    onFieldInput({ name, value }) {
      this.$set(this.fieldValues, name, value)
      this.queueSave()
    },
    openImagePicker(fieldName) {
      this.imagePickerFieldName = fieldName
      this.showImagePicker = true
    },
    handleImageSelected({ fieldName, imageUrl }) {
      this.$set(this.fieldValues, fieldName, imageUrl)
      this.showImagePicker = false
      this.queueSave()
    },
    // Snapshot of what to save and where — captured when the save is queued so a
    // change of selection can't redirect it at the target of the new selection.
    buildSaveContext() {
      if (!this.item) return null
      const values = JSON.parse(JSON.stringify(this.fieldValues))
      if (this.isNative) {
        return { kind: 'cena', idGhCena: this.item.id_gh_cena, templateId: this.templateId, values }
      }
      if (!this.idPlaylist) return null
      return {
        kind: 'mos',
        idPlaylist: this.idPlaylist,
        idItem: this.item.id_playlist_item,
        values,
      }
    },
    queueSave() {
      const context = this.buildSaveContext()
      if (!context) return
      this.cancelQueuedSave()
      this.queuedContext = context
      this.saveState = 'pending'
      this.saveTimer = setTimeout(() => {
        this.saveTimer = null
        this.commit(context)
      }, AUTOSAVE_DELAY)
    },
    cancelQueuedSave() {
      if (this.saveTimer) clearTimeout(this.saveTimer)
      this.saveTimer = null
    },
    flushQueuedSave() {
      if (!this.saveTimer) return
      this.cancelQueuedSave()
      const context = this.queuedContext
      this.queuedContext = null
      if (context) this.commit(context)
    },
    async commit(context) {
      // Responses can land out of order; only the newest save owns the status.
      this.saveSeq += 1
      const seq = this.saveSeq
      this.saveState = 'saving'
      try {
        if (context.kind === 'cena') {
          await this.saveVizrtCena({
            idGhCena: context.idGhCena,
            templateId: context.templateId,
            fieldValues: context.values,
            cod_producao: this.codProducao,
          })
          // Playlist rows render the cena's name/thumbnail, so they need the
          // refresh — but only once the operator has actually stopped editing.
          if (this.codProducao && seq === this.saveSeq && !this.saveTimer) {
            await this.fetchPlaylists(this.codProducao)
          }
        } else {
          await this.updatePlaylistItemValues({
            idPlaylist: context.idPlaylist,
            idItem: context.idItem,
            valores: context.values,
            codProducao: this.codProducao,
          })
        }
        if (seq !== this.saveSeq) return
        this.saveState = 'saved'
        this.saveError = null
      } catch (error) {
        if (seq !== this.saveSeq) return
        this.saveState = 'error'
        this.saveError = error.response?.data?.error || 'Erro ao salvar.'
      }
    },
  },
}
</script>

<style lang="scss" scoped>
// ── Subheader: field filter ──────────────────────────────────────────────
.gg-editor-sub {
  display: flex;
  align-items: center;
  width: 100%;
}

.gg-editor-search {
  display: flex;
  align-items: center;
  width: 496px;
  max-width: 100%;
  height: 56px;
  padding: 0 var(--sc-space-2) 0 var(--sc-space-3);
  background: var(--sc-gg-surface-2);
  border: 1px solid var(--sc-gg-border-100);
  border-radius: var(--sc-radius-md);

  &:focus-within {
    border-color: var(--sc-gg-accent);
  }
}

.gg-editor-search__input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  background: transparent;
  color: var(--sc-gg-text-primary);
  font-size: 16px;
  outline: none;

  &::placeholder {
    color: var(--sc-gg-text-tertiary);
  }
}

.gg-editor-search__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .v-icon {
    color: var(--sc-gg-accent) !important;
  }
}

// ── Body ─────────────────────────────────────────────────────────────────

.gg-editor__note {
  display: flex;
  align-items: center;
  margin: 0 0 var(--sc-space-4);
  font-size: 12px;
  line-height: 16px;
  color: var(--sc-gg-text-tertiary);
}

// Reserved row so the layout doesn't shift as the state cycles.
.gg-editor__status {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 20px;
  margin: var(--sc-space-4) 0 0;
  font-size: 12px;
  line-height: 16px;
  color: var(--sc-gg-text-tertiary);

  ::v-deep .v-progress-circular {
    color: var(--sc-gg-text-tertiary);
  }

  .v-icon {
    color: inherit !important;
  }
}

.gg-editor__status--saved {
  color: var(--sc-gg-success);
}

.gg-editor__status--error {
  color: var(--sc-gg-critical);
}
</style>
